import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
} from 'recharts';
import { wallboardApi } from '../../../api/client';
import TenantPicker, { useTenantFilter } from './TenantPicker';
import HelpButton from '../../help/HelpButton';

const REFRESH_MS = 10_000;

function Stat({ label, value, sub, accent }) {
  return (
    <div className="rounded-thin border border-surface-border dark:border-surface-dark-border bg-surface-panel dark:bg-surface-dark-panel p-4">
      <div className="text-xs uppercase tracking-wider text-surface-muted">{label}</div>
      <div className="text-3xl font-semibold mt-1" style={{ color: accent || undefined }}>{value}</div>
      {sub && <div className="text-xs text-surface-muted mt-1">{sub}</div>}
    </div>
  );
}

export default function TrunksView() {
  const [clientId, setClientId] = useTenantFilter();
  const [queueId, setQueueId] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['wallboard', 'trunks', clientId || '', queueId || ''],
    queryFn: () => wallboardApi.trunks(clientId || undefined, queueId || undefined),
    refetchInterval: REFRESH_MS,
  });

  // Queue options derived from the current rows (inbound queues only).
  const queueOpts = useMemo(() => {
    const m = new Map();
    for (const r of (data?.rows || [])) {
      if (r.queue_id && r.queue_id !== '(outbound)') m.set(r.queue_id, r.queue_name || r.queue_id);
    }
    return [...m.entries()].map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  const inOutPie = useMemo(() => {
    const inb = data?.total_inbound || 0;
    const out = data?.total_outbound || 0;
    const arr = [];
    if (inb) arr.push({ key: 'in', name: 'Inbound', value: inb, fill: '#3b82f6' });
    if (out) arr.push({ key: 'out', name: 'Outbound', value: out, fill: '#14b8a6' });
    return arr;
  }, [data]);

  if (error) return <div className="p-6 text-sm text-accent-red">Failed to load: {error.message}</div>;

  const rows = data?.rows || [];

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold">Trunks — Carrier status</h2>
          <span className="text-xs px-2 py-0.5 rounded-full border border-surface-border text-surface-muted">
            {data?.carrier || 'Telnyx'}
          </span>
          <HelpButton slug="acd-trunks" />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <TenantPicker value={clientId} onChange={(v) => { setClientId(v); setQueueId(''); }} />
          <select
            value={queueId}
            onChange={(e) => setQueueId(e.target.value)}
            className="text-sm rounded-thin border border-surface-border dark:border-surface-dark-border bg-surface-panel dark:bg-surface-dark-panel px-2 py-1"
          >
            <option value="">All queues</option>
            {queueOpts.map((q) => <option key={q.id} value={q.id}>{q.name}</option>)}
          </select>
        </div>
      </div>

      <div className="text-xs text-surface-muted mb-3">
        {data?.range_label || 'Today'} · updates every {REFRESH_MS / 1000}s
        {data?.meta?.failed?.length ? (
          <span className="ml-2 text-amber-600">
            ⚠ some data source(s) unavailable: {data.meta.failed.map((f) => f.cluster).join(', ')}
          </span>
        ) : null}
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-5">
        <Stat label="Inbound (today)" value={data?.total_inbound ?? 0}
              sub={`${data?.total_inbound_answered ?? 0} answered`} accent="#3b82f6" />
        <Stat label="Outbound (today)" value={data?.total_outbound ?? 0}
              sub={`${data?.total_outbound_answered ?? 0} answered`} accent="#14b8a6" />
        <Stat label="Total (today)" value={(data?.total_inbound ?? 0) + (data?.total_outbound ?? 0)} />
        <Stat label="Inbound waiting now" value={data?.live_inbound ?? 0}
              accent={(data?.live_inbound ?? 0) > 0 ? '#f59e0b' : undefined} />
        <Stat label="Answer rate (in)"
              value={data?.total_inbound ? `${Math.round(100 * (data.total_inbound_answered / data.total_inbound))}%` : '—'} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* In vs Out pie */}
        <div className="rounded-thin border border-surface-border dark:border-surface-dark-border bg-surface-panel dark:bg-surface-dark-panel p-4">
          <h3 className="text-xs uppercase tracking-wider text-surface-muted mb-2">Inbound vs Outbound</h3>
          {inOutPie.length === 0 ? (
            <div className="text-xs text-surface-muted py-8 text-center">No carrier calls yet today.</div>
          ) : (
            <div className="flex items-center gap-3">
              <div style={{ width: 160, height: 160 }} className="shrink-0 text-surface-body dark:text-surface-dark-body">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={inOutPie} dataKey="value" nameKey="name" cx="50%" cy="50%"
                         innerRadius={38} outerRadius={70} paddingAngle={1} stroke="none">
                      {inOutPie.map((d) => <Cell key={d.key} fill={d.fill} />)}
                    </Pie>
                    <Tooltip formatter={(v, n) => [`${v} calls`, n]} contentStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <ul className="text-xs space-y-1 min-w-0">
                {inOutPie.map((d) => (
                  <li key={d.key} className="flex items-center gap-2">
                    <span className="inline-block w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: d.fill }} />
                    <span>{d.name}</span>
                    <span className="font-semibold ml-auto">{d.value}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Per-queue breakdown table */}
        <div className="lg:col-span-2 rounded-thin border border-surface-border dark:border-surface-dark-border bg-surface-panel dark:bg-surface-dark-panel p-4 overflow-x-auto">
          <h3 className="text-xs uppercase tracking-wider text-surface-muted mb-2">
            By queue {clientId ? '(tenant-filtered)' : ''}
          </h3>
          {isLoading && !data ? (
            <div className="text-xs text-surface-muted py-6">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="text-xs text-surface-muted py-6">No carrier traffic for this scope today.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-surface-muted text-left">
                <tr>
                  <th className="px-2 py-1">Queue</th>
                  <th className="px-2 py-1">Tenant</th>
                  <th className="px-2 py-1 text-right">Inbound</th>
                  <th className="px-2 py-1 text-right">In answered</th>
                  <th className="px-2 py-1 text-right">Outbound</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.queue_id} className="border-t border-surface-border dark:border-surface-dark-border">
                    <td className="px-2 py-1.5">
                      {r.queue_id === '(outbound)'
                        ? <span className="italic text-surface-muted">{r.queue_name}</span>
                        : <span>{r.queue_name} <span className="text-xs text-surface-muted font-mono">{r.queue_id}</span></span>}
                    </td>
                    <td className="px-2 py-1.5 text-xs">{r.client_name || <span className="text-surface-muted">—</span>}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-blue-600 dark:text-blue-300">{r.inbound || 0}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-surface-muted">{r.inbound_answered || 0}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-teal-600 dark:text-teal-300">{r.outbound || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
